import React from 'react';

const SelectionPrintA4 = ({ selection, customer }) => {
  if (!selection) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not Set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return `${value.length} items`;
    if (typeof value === 'object') {
      if (value.mode && value.amount) {
        return `${value.mode}: ₹${value.amount}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatFieldName = (field) => {
    const fieldMap = {
      'items_description': 'Items Description',
      'customer_remarks': 'Customer Remarks',
      'selection_date': 'Selection Date',
      'customer_id': 'Customer',
      'advance_payments': 'Payments',
      'rate_policy': 'Rate Policy',
      'base24k_rate_ex_gst': 'Base Rate'
    };

    return fieldMap[field] || field
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!selection || !selection.advance_payments) {
      return { totalAdvance: 0, metalCredit: 0 };
    }

    const advancePaid = (selection.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') return sum;
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);

    const metalAdvance = (selection.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit' && p.advance_equivalent) {
        return sum + parseFloat(p.advance_equivalent);
      }
      return sum;
    }, 0);

    const metalCredit = (selection.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit' && p.fine_wt) {
        return sum + parseFloat(p.fine_wt);
      }
      if (p.mode === 'Old Gold' && p.fine_grams) {
        return sum + parseFloat(p.fine_grams);
      }
      return sum;
    }, 0);

    const totalAdvance = advancePaid + metalAdvance;

    return { totalAdvance, metalCredit };
  };

  const totals = calculateTotals();

  // Group payments by mode
  const groupPaymentsByMode = () => {
    const grouped = {};
    (selection.advance_payments || []).forEach(payment => {
      const mode = payment.mode || 'Unknown';
      if (!grouped[mode]) {
        grouped[mode] = [];
      }
      grouped[mode].push(payment);
    });
    return grouped;
  };

  const paymentsByMode = groupPaymentsByMode();

  const renderRevisionChange = (change, idx) => {
    // Selection module uses 'old_value' and 'new_value' (not 'old' and 'new')
    const isPhotosField = change.field === 'photos';
    const oldPhotos = isPhotosField && Array.isArray(change.old_value) ? change.old_value : [];
    const newPhotos = isPhotosField && Array.isArray(change.new_value) ? change.new_value : [];
    
    // Handle photo changes specially
    if (isPhotosField) {
      const photosAdded = newPhotos.length - oldPhotos.length;
      const action = photosAdded > 0 ? 'Photo(s) Added' : photosAdded < 0 ? 'Photo(s) Removed' : 'Photos Changed';
      const color = photosAdded > 0 ? '#059669' : photosAdded < 0 ? '#dc2626' : '#7c3aed';
      
      return (
        <div key={idx} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px', color: color }}>
            📷 {action}
          </div>
          <div style={{ fontSize: '9px' }}>
            <span style={{ color: '#dc2626' }}>{oldPhotos.length} photo{oldPhotos.length !== 1 ? 's' : ''}</span>
            <span style={{ margin: '0 5px' }}>→</span>
            <span style={{ color: '#059669' }}>{newPhotos.length} photo{newPhotos.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      );
    }
    
    // Handle signature fields
    const isSignature = change.field === 'management_signature' || change.field === 'customer_signature';
    
    // Regular field changes
    return (
      <div key={idx} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '3px' }}>{formatFieldName(change.field)}</div>
        {isSignature ? (
          <div style={{ fontSize: '9px' }}>Signature updated</div>
        ) : (
          <div style={{ fontSize: '9px' }}>
            <span style={{ color: '#dc2626' }}>Old: {formatValue(change.old_value)}</span>
            <span style={{ margin: '0 5px' }}>→</span>
            <span style={{ color: '#059669' }}>New: {formatValue(change.new_value)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="selection-a4-print-content" style={{ display: 'none' }}>
      {selection?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{ 
        fontFamily: 'Arial, sans-serif',
        fontSize: '10px',
        lineHeight: '1.3',
        color: '#000',
        padding: '10px',
        maxWidth: '100%'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '3px', margin: '0' }}>SELECTION DETAILS</h1>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>Selection ID: {selection.selection_id}</div>
          <div style={{ fontSize: '11px' }}>Date: {formatDate(selection.selection_date || selection.created_at)} | Status: {selection.status}</div>
        </div>

        {/* Customer & Rate Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <div style={{ border: '1px solid #000', padding: '8px', borderRadius: '4px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #ddd', paddingBottom: '3px', margin: '0 0 6px 0' }}>👤 CUSTOMER</h2>
            {customer && (
              <div style={{ fontSize: '9px', lineHeight: '1.4' }}>
                <div><strong>Name:</strong> {customer.name}</div>
                {customer.mobile && <div><strong>Mobile:</strong> {customer.mobile}</div>}
                {customer.alternate_mobile && <div><strong>Alt:</strong> {customer.alternate_mobile}</div>}
                {customer.email && <div><strong>Email:</strong> {customer.email}</div>}
                {customer.address && <div><strong>Address:</strong> {customer.address}</div>}
              </div>
            )}
          </div>

          {selection.base24k_rate_ex_gst && (
            <div style={{ border: '1px solid #f59e0b', padding: '8px', borderRadius: '4px', backgroundColor: '#fffbeb' }}>
              <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #f59e0b', paddingBottom: '3px', margin: '0 0 6px 0' }}>💰 RATE INFO</h2>
              <div style={{ fontSize: '9px', lineHeight: '1.4' }}>
                {selection.rate_policy && (
                  <div><strong>Policy:</strong> {
                    selection.rate_policy === 'lock-today' ? 'Booked' : 'At Delivery'
                  }</div>
                )}
                <div><strong>24K Rate:</strong> ₹{formatCurrency(selection.base24k_rate_ex_gst)}/g</div>
                {selection.rate_locked_date && <div><strong>Lock Date:</strong> {formatDate(selection.rate_locked_date)}</div>}
                {selection.gst_applicable === false ? (
                  <div><strong>GST:</strong> Kaccha (No GST)</div>
                ) : (
                  <div><strong>GST:</strong> {selection.gst_percent_snapshot || 3}% {selection.gst_input_style === 'INCL' ? '(Incl)' : '(Excl)'}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Item Photos */}
        {selection.photos && selection.photos.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 8px 0' }}>📷 ITEM PHOTOS ({selection.photos.length})</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {selection.photos.map((photo, idx) => (
                <div key={idx} style={{ border: '1px solid #ddd', padding: '4px', borderRadius: '4px' }}>
                  <img 
                    src={photo} 
                    alt={`Photo ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '180px',
                      objectFit: 'cover',
                      borderRadius: '2px'
                    }}
                  />
                  <div style={{ fontSize: '8px', textAlign: 'center', marginTop: '3px' }}>Photo #{idx + 1}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items Description */}
        {selection.items_description && (
          <div style={{ marginBottom: '15px', border: '1px solid #ddd', padding: '8px', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', margin: '0 0 6px 0' }}>📝 ITEMS DESCRIPTION</h2>
            <div style={{ fontSize: '9px', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {selection.items_description}
            </div>
          </div>
        )}

        {/* Customer Remarks */}
        {selection.customer_remarks && (
          <div style={{ marginBottom: '15px', border: '1px solid #f59e0b', padding: '8px', borderRadius: '4px', backgroundColor: '#fef3c7' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', margin: '0 0 6px 0' }}>💬 CUSTOMER REMARKS</h2>
            <div style={{ fontSize: '9px', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {selection.customer_remarks}
            </div>
          </div>
        )}

        {/* Payments & Deposits */}
        {selection.advance_payments && selection.advance_payments.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 8px 0' }}>💳 PAYMENTS & DEPOSITS</h2>

            {Object.keys(paymentsByMode).map((mode, modeIdx) => (
              <div key={modeIdx} style={{ marginBottom: '10px', border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                <h3 style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px', color: '#059669', margin: '0 0 4px 0' }}>{mode}</h3>

                {mode === 'Old Gold' ? (
                  <div style={{ fontSize: '8px' }}>
                    {paymentsByMode[mode].map((payment, payIdx) => (
                      <div key={payIdx} style={{ marginBottom: '6px' }}>
                        <div><strong>Date:</strong> {formatDate(payment.date)} | <strong>Amt:</strong> ₹{formatCurrency(payment.amount || 0)}</div>
                        {payment.items && payment.items.length > 0 && (
                          <div style={{ marginTop: '3px', marginLeft: '8px' }}>
                            {payment.items.map((item, itemIdx) => (
                              <div key={itemIdx} style={{ marginBottom: '2px', fontSize: '7px' }}>
                                {item.item_name} - Gross: {item.gross_wt}g, Net: {item.net_wt}g, Fine: {item.fine_wt}g ({item.purity}%)
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : mode === 'Metal Credit' ? (
                  <div style={{ fontSize: '8px' }}>
                    {paymentsByMode[mode].filter(p => parseFloat(p.amount || 0) > 0).map((payment, payIdx) => (
                      <div key={payIdx} style={{ marginBottom: '3px' }}>
                        {formatDate(payment.date)} | Fine: {payment.fine_wt || 0}g | ₹{formatCurrency(payment.amount || 0)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '8px' }}>
                    {paymentsByMode[mode].map((payment, payIdx) => {
                      const amount = parseFloat(payment.amount) || 0;
                      const isRefund = payment.transaction_type === 'REFUND';
                      return (
                        <div key={payIdx} style={{ marginBottom: '3px', color: isRefund ? '#dc2626' : '#000' }}>
                          {formatDate(payment.date)} | {isRefund ? '-' : ''}₹{formatCurrency(Math.abs(amount))}
                          {isRefund && ' (REFUND)'}
                          {payment.upi_ref && ` | Ref: ${payment.upi_ref}`}
                          {payment.note && ` | ${payment.note}`}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', fontSize: '9px' }}>
                  Total: ₹{formatCurrency(
                    paymentsByMode[mode].reduce((sum, p) => {
                      const amount = parseFloat(p.amount) || 0;
                      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
                    }, 0)
                  )}
                </div>
              </div>
            ))}

            {/* Net Advance Summary */}
            <div style={{ marginTop: '10px', border: '2px solid #000', padding: '8px', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
              <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', margin: '0 0 6px 0' }}>💰 ADVANCE SUMMARY</h2>
              <div style={{ fontSize: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span>Total Advance:</span>
                  <span style={{ fontWeight: 'bold', color: '#059669' }}>₹{formatCurrency(totals.totalAdvance)}</span>
                </div>
                {totals.metalCredit > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                    <span>Metal Credit:</span>
                    <span style={{ fontWeight: 'bold', color: '#d97706' }}>{totals.metalCredit.toFixed(2)}g</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Signatures */}
        {(selection.management_signature || selection.customer_signature) && (
          <div style={{ marginBottom: '15px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 6px 0' }}>✍️ SIGNATURES</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {selection.management_signature && (
                <div style={{ border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '4px' }}>Management</div>
                  <img src={selection.management_signature} alt="Management" style={{ width: '100%', maxWidth: '120px', height: 'auto', maxHeight: '40px', border: '1px solid #ddd' }} />
                </div>
              )}
              {selection.customer_signature && (
                <div style={{ border: '1px solid #ddd', padding: '6px', borderRadius: '4px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '4px' }}>Customer</div>
                  <img src={selection.customer_signature} alt="Customer" style={{ width: '100%', maxWidth: '120px', height: 'auto', maxHeight: '40px', border: '1px solid #ddd' }} />
                  {customer && customer.name && <div style={{ fontSize: '8px', marginTop: '3px' }}>Name: {customer.name}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revision History */}
        {selection.revision_history && selection.revision_history.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            <h2 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #000', paddingBottom: '3px', margin: '0 0 6px 0' }}>📝 REVISION HISTORY</h2>
            {selection.revision_history.map((revision, idx) => {
              const changes = revision.changes || [];
              return (
                <div key={idx} style={{ marginBottom: '10px', border: '1px solid #ddd', padding: '6px', borderRadius: '4px', backgroundColor: '#fef3c7' }}>
                  <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>Revision #{revision.revision_number || idx + 1} - {formatDate(revision.timestamp)}</div>
                  <div style={{ fontSize: '8px', marginBottom: '4px' }}>
                    <strong>By:</strong> {revision.edited_by || 'Unknown'}
                    {revision.approved_by && ` | Approved by: ${revision.approved_by}`}
                  </div>
                  {changes.length > 0 && (
                    <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb' }}>
                      {changes.map((change, changeIdx) => renderRevisionChange(change, changeIdx))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '10px', paddingTop: '6px', borderTop: '1px solid #000', textAlign: 'center', fontSize: '8px', color: '#6b7280' }}>
          <div>Computer-generated document | Printed: {new Date().toLocaleString('en-IN')}</div>
        </div>

      </div>
    </div>
  );
};

export default SelectionPrintA4;